import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { List, ListItem, Table } from "@mantine/core";

import { Fragment } from "react/jsx-runtime";

import { TextWrapper } from "@/components/TextWrapper";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getEstimatedBloodLossRowValues } from "@/encounters/components/EncounterLabor/utils/labor";

import TimeCell from "../../../EncounterLaborTable/LaborRow/Common/TimeCell";

interface ClientEstimatedBloodLossRowProps {
  time: string;
  observation: Observation | undefined;
  showNumbersOnly?: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
  isViewOnly: boolean;
}

export default function ClientEstimatedBloodLossRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  onRefresh,
  isViewOnly,
}: ClientEstimatedBloodLossRowProps) {
  const { estimated, quantitative, hasAnswers, hasNotes, notes } = useMemo(
    () => getEstimatedBloodLossRowValues(observation),
    [observation],
  );

  if (!observation) return null;

  const previousVersions = hasPreviousVersions(observation);

  if (!hasAnswers && !hasNotes) return null;

  const showDetailsRow = !showNumbersOnly && (hasAnswers || hasNotes);

  const versionCell = (
    <>
      <TextWrapper pt="xs" size="sm">
        Estimated Blood Loss:
      </TextWrapper>
      <List fz="sm" withPadding>
        {estimated && (
          <ListItem>
            <strong>Estimated:</strong> {estimated}
          </ListItem>
        )}
        {quantitative && (
          <ListItem>
            <strong>Quantitative:</strong> {quantitative}
          </ListItem>
        )}
      </List>
    </>
  );

  const notesCell = (
    <TextWrapper pt="xs" size="sm">
      Estimated Blood Loss Comments: {notes}
    </TextWrapper>
  );

  const signatureCell =
    showPreviousVersions && previousVersions ? (
      <PreviousVersions
        observation={observation}
        rowComponent={ClientEstimatedBloodLossRow}
        showPreviousVersions={showPreviousVersions}
      />
    ) : (
      <LastUpdated observation={observation} showPreviousVersions={false} />
    );

  const detailsRowNeeded = showDetailsRow;

  return (
    <Fragment>
      {hasAnswers && showPreviousVersions && (
        <Table.Tr bg="white">
          <TimeCell
            time={time}
            isViewOnly={isViewOnly}
            observation={observation}
            rowSpan={detailsRowNeeded ? 2 : 1}
          />
          <Table.Td />
          <Table.Td />
          <Table.Td />
          <Table.Td />
          <Table.Td>{estimated}</Table.Td>
        </Table.Tr>
      )}
      {detailsRowNeeded && (
        <Table.Tr bg="white" className={classes.tableRow}>
          <Table.Td p="md" colSpan={5}>
            {versionCell}
            {hasNotes && notesCell}
            {signatureCell}
          </Table.Td>
        </Table.Tr>
      )}
    </Fragment>
  );
}
